import { Autocomplete, Chip, CircularProgress, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import CustomModal, { CustomModalBasicProps, GridItem, GridSection } from "src/components/CustomModal";
import useObjectState from "src/hooks/useObjectState";
import FhirOrganizationActivationFlow from "./activation-flow/OrganizationActivationFlow";
import { Section } from "src/utils/layout";
import { WrappedOrganization } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization";
import { EditableCard, InfoItem } from "src/careflow/common/EditableCard";
import { useValueSet } from "src/careflow/ph-core/fhir-client/fhir-react";
import { ValueSetWrapper } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet";
import Iconify from "src/components/Iconify";

export interface FhirOrganizationDetailsModalProps extends CustomModalBasicProps {
  organization: WrappedOrganization | null;
  updateOrganization: Function;
  refresh: Function;
  translate: any;
}

interface FhirOrganizationDetailsState {
  active: boolean;
  name: string | null;
  type: any;
  alias: string[] | null;
}

interface ErrorFieldState {
  active: string | null;
  name: string | null;
}

interface DisplayModeProps {
  translate: any;
  organization: {
    main: WrappedOrganization | null;
    partOf: WrappedOrganization | null;
  },
}

const DisplayMode = ({ translate, organization }: DisplayModeProps) => {
  const { main, partOf } = organization ?? {};
  return (
    <Grid container spacing={3} sx={{ marginBottom: 1 }}>
      <Grid item xs={6}>
        <InfoItem 
          label={translate('details.general.alias','admin')} 
          value={main?.alias?.map((e) => e)?.[0]} 
        />
      </Grid>
      <Grid item xs={6}>
        <InfoItem 
          label={translate('details.general.partOf','admin')} 
          value={partOf?.name} 
        />
      </Grid>
      <Grid item xs={6}>
        <InfoItem 
          label={translate('details.general.type','admin')} 
          value={main?.getType()}
        />
      </Grid>
    </Grid>
  );
};

export const EditMode = ({
  open,
  organization,
  updateOrganization,
  refresh,
  translate,
  onClose,
  ...modalProps
}: FhirOrganizationDetailsModalProps) => {

  const [{
    type,
    alias,
    active,
    name
  }, updateState] = useObjectState<FhirOrganizationDetailsState>({
    type: null,
    alias: null,
    active: true,
    name: null
  });

  const [errors, updateErrorState] = useObjectState<ErrorFieldState>({
    name: null,
    active: null
  });

  const [valueSet] = useValueSet('organization-types',
    {map: ValueSetWrapper,autofetch: !!open}
  );

  useEffect(() => {
    updateErrorState({ name: null });
    if(!organization && !open) return;
    updateState({
      name: organization?.name,
      active: organization?.active ?? true,
      type: organization?.type?.map(({ coding }) => coding?.[0]) ?? null,
      alias: organization?.alias
    });
  },[organization, open])

  const handleUpdate = async () => {

    let mapType: any = [];

    if(type?.length > 0){
      mapType = type?.map((e) => ({
        coding: [e],
        text: e?.display
      }));
    }

    if(!name){
      updateErrorState({ name: translate('dialogs.name')});
      return;
    }

    await updateOrganization({
      resourceType: 'Organization',
      name,
      type: type?.length > 0 ? mapType : null,
      alias,
      active
    });

    onClose?.({},'backdropClick');
  };

  return (
    <CustomModal
      open={open}
      title={translate('details.title')}
      breadcrumbs={[""]}
      isLoading={false}
      onSave={handleUpdate}
      onCancel={onClose as Function}
      onClose={onClose}
      {...modalProps}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <GridSection>
            <GridItem>
              <TextField
                fullWidth
                id="name"
                name="name"
                label={translate('details.general.name')}
                error={!!errors.name}
                helperText={errors.name && errors.name}
                variant="outlined"
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const { value } = event.target;
                  if (value.trim().length) updateErrorState({ name: null });
                  updateState({
                    name: event.target.value,
                  });
                }}
              />
            </GridItem>
            <GridItem>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={alias ?? []}
              onChange={(_, newValue) => {
                const cleaned = newValue
                  .map(v => v.trim())
                  .filter(Boolean);

                updateState({ alias: [...new Set(cleaned)] });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('details.general.alias')}
                  placeholder={translate('details.general.aliasPlaceHolder')}
                />
              )}
              filterOptions={(options, state) => {
                const filteredOptions = options.filter((i) =>
                  i?.toLowerCase().includes(state.inputValue.toLowerCase())
                );
              
                const input = state.inputValue?.trim();
                const alreadyExists = options.some(
                  (opt) => opt.toLowerCase() === input.toLowerCase()
                );
              
                if (input && !alreadyExists) {
                  return [
                    ...filteredOptions,
                    input
                  ];
                }
              
                return filteredOptions;
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props}>
                    <>
                      <Iconify icon="ic:baseline-add" style={{ marginRight: 8 }} />
                      Add&nbsp;<strong>"{option}"</strong>
                    </>
                  </li>
                )
              }}
            />
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <Autocomplete
                disabled={!valueSet}
                multiple
                freeSolo
                value={type ?? []}
                onChange={(_: React.SyntheticEvent, newValue) => {
                  updateState({ type: newValue });
                }}
                options={valueSet?.asList() ?? []}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : option?.display ?? ""
                }
                isOptionEqualToValue={(option, value) =>
                  typeof option !== "string" &&
                  typeof value !== "string" &&
                  option.code === value.code
                }
                filterOptions={(options, params) =>
                  options?.filter((item) =>
                    item?.display
                      ?.toLowerCase()
                      .includes(params.inputValue.toLowerCase())
                  )
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });

                    const label =
                      typeof option === "string"
                        ? option
                        : option?.coding?.[0]?.display ??
                          option?.display ??
                          "";

                    return (
                      <Chip
                        {...tagProps}
                        key={index}
                        size="small"
                        label={label}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('details.general.type')}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {!valueSet?.asListAll?.()?.length && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </GridItem>
          </GridSection>
        </Grid>
        <Grid item xs={12}>
          <Section fullWidth>
            <FhirOrganizationActivationFlow
              organization={organization}
              refresh={refresh}
              translate={translate}
            />
          </Section>
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default function FhirOrganizationInformation({
  organization,
  updateOrganization,
  refresh,
  translate
}: {
  organization: {
    main: WrappedOrganization | null;
    partOf: WrappedOrganization | null;
  },
  updateOrganization: Function;
  refresh: Function;
  translate: any;
}) {

  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <EditableCard
        title={translate('details.title')}
        showEditButton={true}
        onEditButtonPress={() => setOpenModal(true)}
      >
        <DisplayMode translate={translate} organization={organization} />
      </EditableCard>
      <EditMode 
        translate={translate}
        open={openModal}
        organization={organization?.main}
        updateOrganization={updateOrganization}
        refresh={refresh}
        onClose={() => setOpenModal(false)}
      />
    </>
  )
};